import { InfoBanner, Modal, Typography, ZeroState } from "@codat/orchard-ui";
import { useManualIntegrationsQuery } from "api/company";
import classNames from "classnames";
import { canSkip } from "contexts/config-fn";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { useDemoMode } from "contexts/DemoModeContext";
import { SourceTypeStatus } from "contexts/SourceTypesContext";
import { useGATrackingEvent } from "hooks/useGATracking";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import { useSourceTypeDisplayProps } from "hooks/useSourceTypeDisplayProps";
import { useEffect, useState } from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";
import { formatUrl, isDemoModeValid, usePageViewedEvent } from "utils";

import ExitModal from "components/ExitModal";
import LinkPageRoot from "components/LinkPageRoot";
import WarningDialog from "components/WarningDialog";
import {
  WithSourceTypeEvent,
  WithSourceTypeInnerProps,
} from "components/WithSourceTypeEventContext";
import {
  CompanyParams,
  CompanySourceTypeParams,
  ConsentParams,
  routes,
} from "config/urls";

import { FileUploadLink } from "../FileUpload";
import styles from "./CompanyLink.module.css";
import LinkCompanyContainer from "./LinkCompanyContainer";
import LinkCompanySearchContainer from "./LinkCompanySearchContainer";

type CompanyLinkContentProps = RouteComponentProps<CompanySourceTypeParams> &
  WithSourceTypeInnerProps;

export function CompanyLinkContent({
  match,
  sourceTypes,
  sourceTypesStatus,
  currentSourceType,
  nextPrioritySourceType,
}: CompanyLinkContentProps) {
  const history = useHistory();
  const { state } = useConfig();
  const { logGAEvent } = useGATrackingEvent();

  const [isWarningModal, setWarningModal] = useState(false);
  const [isExitModal, setExitModal] = useState(false);
  const { isDemo } = useDemoMode();

  const displayProps = useSourceTypeDisplayProps(currentSourceType);

  const [
    { data: integrationsData, loading: isIntegrationsLoading },
    executeGetIntegrations,
  ] = useManualIntegrationsQuery(currentSourceType?.integrationsUri ?? "");

  useEffect(() => {
    if (currentSourceType && executeGetIntegrations) {
      executeGetIntegrations();
    }
  }, [executeGetIntegrations, currentSourceType]);

  const isComplete =
    !!currentSourceType &&
    !isIntegrationsLoading &&
    sourceTypesStatus === SourceTypeStatus.READY &&
    state.status === ConfigStatusType.READY;

  usePageViewedEvent({
    ready:
      !!currentSourceType?.sourceType &&
      !!state.data &&
      !!sourceTypes &&
      !!integrationsData &&
      isComplete,
    clientConfig: state.data,
    sourceTypes,
    integrations: integrationsData,
    resetOnChange: currentSourceType?.sourceType,
  });

  const locationSearch = useLocation().search;
  const [integrationKey, setIntegrationKey] = useState<string>("");

  const handleSkip = useNextSourceTypeRedirectHandler(
    match.params.companyId,
    nextPrioritySourceType,
    currentSourceType && !nextPrioritySourceType
  );

  const secondaryCtaProps = (() => {
    if (canSkip(currentSourceType, state.data)) {
      return {
        label: "Skip",
        "data-testid": "skip-btn",
        "aria-label": "Skip",
        onClick: handleSkip,
        analyticsName: "Skip",
      };
    } else {
      return undefined;
    }
  })();

  const handleCancel = () => {
    history.push({
      pathname: formatUrl<CompanyParams>(routes.company.revisit, {
        companyId: match.params.companyId,
      }),
      search: locationSearch,
    });
  };

  const onIntegrationSelect = (selectedIntegrationKey: string) => {
    setIntegrationKey(selectedIntegrationKey);
  };

  const handleContinue = () => {
    history.push({
      pathname: formatUrl<ConsentParams>(routes.company.consent, {
        companyId: match.params.companyId,
        platformKey: integrationKey,
      }),
      search: locationSearch,
    });
  };

  const onNextButtonClick = () => {
    if (currentSourceType?.displayWarning) {
      setWarningModal(true);
      return;
    }

    handleContinue();
  };

  const demoModeValid = (() => {
    const sourceType = currentSourceType?.sourceType;

    if (!sourceType) {
      return true;
    }

    const integrationKeys = integrationsData?.results?.map((i) => i.key) ?? [];

    return isDemoModeValid(isDemo, integrationKeys, sourceType);
  })();

  const isNextButtonDisabled = !integrationKey || !demoModeValid;

  const handleExitButtonClick = state.data.hasCustomRedirect
    ? () => {
        logGAEvent("Link Flow", "ExitIcon", match.params.companyId);
        setExitModal(true);
      }
    : undefined;

  const showSearch = integrationsData && integrationsData?.results?.length > 4;
  const fileUploadOnly =
    (integrationsData &&
      integrationsData.totalResults === 0 &&
      displayProps.link.canUpload) ??
    false;
  const isEmpty =
    integrationsData?.totalResults === 0 && !displayProps.link.canUpload;

  const instructionsText = (() => {
    const defaultText =
      "You can upload requested PDFs, Excel files, or CSVs below.";

    const { fileUpload } = state.data.pageConfiguration;

    switch (currentSourceType?.sourceType) {
      case "Accounting":
        return fileUpload.accounting?.body ?? defaultText;

      case "Banking":
        return fileUpload.banking?.body ?? defaultText;

      case "Other":
        return fileUpload.businessDocuments?.body ?? defaultText;

      default:
        return defaultText;
    }
  })();
  return (
    <>
      <LinkPageRoot
        isComplete={isComplete}
        title={
          <Typography
            key={match.params.sourceType}
            data-testid="page-title"
            className={styles.title}
            variant="h3"
            as="h2"
          >
            {fileUploadOnly
              ? displayProps.link.fileUploadText
              : displayProps.link.sourceText}
          </Typography>
        }
        showLinkedAccounts
        onExitClick={handleExitButtonClick}
        secondaryCtaProps={secondaryCtaProps}
        primaryCtaProps={{
          onClick: onNextButtonClick,
          disabled: isNextButtonDisabled,
          hidden: isNextButtonDisabled,
          analyticsName: "Next",
          eventProperties: {
            selectedIntegrationKey: integrationKey,
          },
        }}
        currentSourceType={currentSourceType}
      >
        {!demoModeValid && (
          <div data-testid="invalid-info-banner">
            <InfoBanner
              color="negative"
              title="Invalid demo mode setup"
              subtitle="You are using demo mode without a valid setup. Please ensure the
            correct sandbox integration has been enabled and your client is set
            up to use four-letter platform keys."
            />
          </div>
        )}
        {fileUploadOnly && currentSourceType && (
          <Typography
            as="p"
            variant="body_md"
            className={styles.subText}
            data-testid="file-upload-instructions"
          >
            {instructionsText}
          </Typography>
        )}
        {fileUploadOnly && (
          <Typography
            as="p"
            variant="body_md"
            className={styles.subText}
            data-testid="file-upload-subtext"
          >
            {displayProps.link.fileUploadSubtext}
          </Typography>
        )}
        {sourceTypes && integrationsData && !showSearch && (
          <LinkCompanyContainer
            onIntegrationSelect={onIntegrationSelect}
            selectedIntegration={integrationKey}
            integrations={integrationsData.results}
            className={styles.container}
            clientName={state.data.clientName}
            link={state.data.help?.link}
          />
        )}
        {sourceTypes && integrationsData && showSearch && (
          <LinkCompanySearchContainer
            onIntegrationSelect={onIntegrationSelect}
            selectedIntegration={integrationKey}
            integrations={integrationsData.results}
            className={classNames(
              styles.container,
              styles.searchContainer,
              styles.fadeout
            )}
          />
        )}
        {displayProps.link.canUpload && currentSourceType && (
          <FileUploadLink
            sourceType={currentSourceType.sourceType}
            fileUploadOnly={fileUploadOnly}
          />
        )}
        {isEmpty && (
          <ZeroState
            className={styles.zeroState}
            title="No options available"
            description="Please contact support"
            data-testid="no-integrations-indicator"
          />
        )}
      </LinkPageRoot>
      <Modal onClose={() => setWarningModal(false)}>
        {isWarningModal && (
          <WarningDialog
            title={displayProps.link.warningTitle}
            subText={displayProps.link.warningMessage}
            primaryCtaProps={{
              label: "Back",
              onClick: handleCancel,
              analyticsName: "Back",
            }}
            secondaryCtaProps={{
              label: "Yes, continue",
              onClick: handleContinue,
              analyticsName: "Continue",
            }}
          />
        )}
      </Modal>
      {state.data.hasCustomRedirect && (
        <ExitModal
          isOpen={isExitModal}
          onClose={() => setExitModal(false)}
          companyId={match.params.companyId}
          sourceType={currentSourceType?.sourceType}
        />
      )}
    </>
  );
}

export const CompanyLink: React.FC<
  RouteComponentProps<CompanySourceTypeParams>
> = (props) => {
  return (
    <WithSourceTypeEvent
      sourceType={props.match.params?.sourceType}
      eventName={(sourceType) => `Link ${sourceType} package page`}
    >
      {(sourceTypeProps) => (
        <CompanyLinkContent {...sourceTypeProps} {...props} />
      )}
    </WithSourceTypeEvent>
  );
};
